import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { ClipboardList, Check, ChevronDown, ChevronUp, Save, Loader2, Plus, Trash2, CheckCircle2, XCircle, Sparkles, UserCheck, UserX } from 'lucide-react';
import { AnecdotePhotoUpload } from './AnecdotePhotoUpload';
import { ProtagonistPhotoUpload } from './ProtagonistPhotoUpload';
import { AnecdoteAIAssistant } from './AnecdoteAIAssistant';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTOSAVE_DEBOUNCE_MS = 1500;

const ClientFormPanel = ({
  bookId,
  bookStatus,
  clientFormCompleted,
  onFormCompleted,
  onSuggestionResolved,
  recipientName,
  recipientLastName,
  bookProfile,
  // Co-promoter mode (token-based, public access)
  coPromoterToken,
  coPromoterName,
  hideHelpersSection = false,
}) => {
  const { t } = useTranslation();
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState('idle'); // idle | saving | saved | error
  const [expanded, setExpanded] = useState(!clientFormCompleted);
  const [expandedSections, setExpandedSections] = useState({});
  const [repeatCounts, setRepeatCounts] = useState({});
  const [errorKeys, setErrorKeys] = useState(new Set());
  const [coPromoters, setCoPromoters] = useState([]);
  const [showWelcome, setShowWelcome] = useState(false);
  // Co-promoter suggestions indexed by `${section_id}|${field_id || '__section__'}`
  // so each field render can display its aportaciones inline.
  const [fieldSuggestions, setFieldSuggestions] = useState({});
  const [busySuggestionId, setBusySuggestionId] = useState(null);
  const isCoPromoterMode = Boolean(coPromoterToken);
  const apiBase = isCoPromoterMode
    ? `${API_URL}/api/co-promote/${coPromoterToken}`
    : `${API_URL}/api/books/${bookId}`;
  const formEndpoint = `${apiBase}/client-form`;

  const clearError = (key) => setErrorKeys(prev => {
    if (!prev.has(key)) return prev;
    const next = new Set(prev);
    next.delete(key);
    return next;
  });

  const autosaveTimer = useRef(null);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    fetchForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  // ---- Auto-save with debounce ----
  const triggerAutosave = useCallback((data) => {
    if (!initialLoadDone.current) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setAutosaveStatus('saving');
    autosaveTimer.current = setTimeout(async () => {
      try {
        await axios.post(
          formEndpoint,
          { data, is_draft: true },
          { withCredentials: !isCoPromoterMode },
        );
        setAutosaveStatus('saved');
      } catch {
        setAutosaveStatus('error');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [formEndpoint, isCoPromoterMode]);

  useEffect(() => {
    if (initialLoadDone.current) {
      triggerAutosave(formData);
    }
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [formData, triggerAutosave]);

  const fetchForm = async () => {
    try {
      const res = await axios.get(formEndpoint, { withCredentials: !isCoPromoterMode });
      setTemplate(res.data.template);
      setFormData(res.data.data || {});
      const counts = {};
      if (res.data.template?.sections) {
        for (const section of res.data.template.sections) {
          if (section.repeatable && Array.isArray(res.data.data?.[section.id])) {
            counts[section.id] = res.data.data[section.id].length;
          }
        }
      }
      setRepeatCounts(counts);
      if (!res.data.completed && res.data.template?.sections?.[0]) {
        setExpandedSections({ [res.data.template.sections[0].id]: true });
      }
      // First-visit welcome modal (per book, persisted in localStorage)
      try {
        const welcomeKey = `agom-welcome-${bookId || coPromoterToken}`;
        if (!isCoPromoterMode && !localStorage.getItem(welcomeKey)) {
          setShowWelcome(true);
        }
      } catch { /* localStorage disabled */ }
    } catch {
      console.error('Error loading client form');
    } finally {
      setLoading(false);
      // Defer flag flip so the first useEffect cycle does not autosave.
      setTimeout(() => { initialLoadDone.current = true; }, 100);
    }
  };

  const dismissWelcome = () => {
    try {
      const welcomeKey = `agom-welcome-${bookId || coPromoterToken}`;
      localStorage.setItem(welcomeKey, '1');
    } catch { /* */ }
    setShowWelcome(false);
  };

  // ---- Co-promoter list (only in promoter mode) ----
  const fetchCoPromoters = useCallback(async () => {
    if (isCoPromoterMode || !bookId) return;
    try {
      const res = await axios.get(`${API_URL}/api/books/${bookId}/co-promoters`, { withCredentials: true });
      setCoPromoters(res.data?.co_promoters || []);
    } catch { /* */ }
  }, [bookId, isCoPromoterMode]);

  useEffect(() => { fetchCoPromoters(); }, [fetchCoPromoters]);

  // ---- Co-promoter suggestions (only in promoter mode) ----
  // Promoter sees approved + pending + rejected aportaciones grouped by field
  // so every form field shows the voice of the promoter plus any co-promoter
  // that has contributed to it.
  const fetchFieldSuggestions = useCallback(async () => {
    if (isCoPromoterMode || !bookId) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/books/${bookId}/co-promoter-suggestions`,
        { withCredentials: true },
      );
      const bySlot = {};
      (res.data?.suggestions || []).forEach(s => {
        const slotKey = `${s.section_id}|${s.field_id || '__section__'}`;
        if (!bySlot[slotKey]) bySlot[slotKey] = [];
        bySlot[slotKey].push(s);
      });
      setFieldSuggestions(bySlot);
    } catch { /* silent */ }
  }, [bookId, isCoPromoterMode]);

  useEffect(() => { fetchFieldSuggestions(); }, [fetchFieldSuggestions]);

  const approveFieldSuggestion = async (sid) => {
    setBusySuggestionId(sid);
    try {
      await axios.post(
        `${API_URL}/api/books/${bookId}/co-promoter-suggestions/${sid}/approve`,
        {}, { withCredentials: true },
      );
      toast.success('Aportación aceptada');
      fetchFieldSuggestions();
      onSuggestionResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aceptar');
    } finally {
      setBusySuggestionId(null);
    }
  };

  const rejectFieldSuggestion = async (sid) => {
    setBusySuggestionId(sid);
    try {
      await axios.post(
        `${API_URL}/api/books/${bookId}/co-promoter-suggestions/${sid}/reject`,
        {}, { withCredentials: true },
      );
      toast.message('Aportación descartada');
      fetchFieldSuggestions();
      onSuggestionResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo descartar');
    } finally {
      setBusySuggestionId(null);
    }
  };

  const toggleCoPromoter = async (cid, active) => {
    try {
      await axios.patch(`${API_URL}/api/books/${bookId}/co-promoters/${cid}`,
        { active }, { withCredentials: true });
      toast.success(active ? 'Permisos restaurados' : 'Permisos revocados');
      fetchCoPromoters();
    } catch {
      toast.error('No se pudo actualizar el permiso');
    }
  };

  const updateField = (sectionId, fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), [fieldId]: value },
    }));
    clearError(`${sectionId}.${fieldId}`);
  };

  const updateRepeatField = (sectionId, index, fieldId, value) => {
    setFormData(prev => {
      const arr = [...(prev[sectionId] || [])];
      if (!arr[index]) arr[index] = {};
      arr[index] = { ...arr[index], [fieldId]: value };
      return { ...prev, [sectionId]: arr };
    });
    clearError(`${sectionId}.${index}.${fieldId}`);
  };

  const updateInlineRepeatField = (sectionId, parentFieldId, index, fieldId, value) => {
    setFormData(prev => {
      const sectionData = { ...(prev[sectionId] || {}) };
      const arr = [...(sectionData[parentFieldId] || [])];
      if (!arr[index]) arr[index] = {};
      arr[index] = { ...arr[index], [fieldId]: value };
      sectionData[parentFieldId] = arr;
      return { ...prev, [sectionId]: sectionData };
    });
    clearError(`${sectionId}.${parentFieldId}.${index}.${fieldId}`);
  };

  const addRepeatItem = (sectionId, maxRepeat) => {
    const current = repeatCounts[sectionId] || 1;
    if (current < maxRepeat) {
      setRepeatCounts(prev => ({ ...prev, [sectionId]: current + 1 }));
    }
  };

  const removeRepeatItem = (sectionId, index) => {
    setFormData(prev => {
      const arr = [...(prev[sectionId] || [])];
      arr.splice(index, 1);
      return { ...prev, [sectionId]: arr };
    });
    setRepeatCounts(prev => ({ ...prev, [sectionId]: Math.max(1, (prev[sectionId] || 1) - 1) }));
  };

  const validateRequired = () => {
    if (!template?.sections) return { ok: true, missing: [] };
    const missing = [];
    const isVisibleField = (f, ctx) => isFieldVisible(f, ctx);

    for (const section of template.sections) {
      const sectionData = formData[section.id];
      if (section.repeatable) {
        const items = Array.isArray(sectionData) ? sectionData : [];
        items.forEach((row, idx) => {
          for (const f of section.fields) {
            if (!f.required) continue;
            if (!isVisibleField(f, row)) continue;
            const v = row?.[f.id];
            const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
            if (empty) missing.push({ key: `${section.id}.${idx}.${f.id}`, label: `${section.title} #${idx + 1} → ${f.label}` });
          }
        });
      } else {
        for (const f of section.fields) {
          if (f.type === 'repeatable_inline') {
            const arr = Array.isArray((sectionData || {})[f.id]) ? (sectionData || {})[f.id] : [];
            arr.forEach((row, idx) => {
              for (const sub of (f.fields || [])) {
                if (!sub.required) continue;
                if (!isVisibleField(sub, row)) continue;
                const v = row?.[sub.id];
                const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
                if (empty) missing.push({ key: `${section.id}.${f.id}.${idx}.${sub.id}`, label: `${f.label} #${idx + 1} → ${sub.label}` });
              }
            });
            continue;
          }
          if (f.type === 'anecdote_list') {
            const arr = Array.isArray((sectionData || {})[f.id]) ? (sectionData || {})[f.id] : [];
            arr.forEach((row, idx) => {
              // Legacy items (free text only, pre-restructure) stay valid.
              const isLegacy = !!(row?.text || '').trim() && !(row?.what_happened || '').trim() && !(row?.title || '').trim();
              if (isLegacy) return;
              for (const sub of (f.fields || [])) {
                if (!sub.required) continue;
                const v = row?.[sub.id];
                const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
                if (empty) missing.push({ key: `${section.id}.${f.id}.${idx}.${sub.id}`, label: `${section.title} → Anécdota #${idx + 1} → ${sub.label}` });
              }
            });
            continue;
          }
          if (!f.required) continue;
          if (!isVisibleField(f, sectionData)) continue;
          const v = (sectionData || {})[f.id];
          const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
          if (empty) missing.push({ key: `${section.id}.${f.id}`, label: `${section.title} → ${f.label}` });
        }
      }
    }
    return { ok: missing.length === 0, missing };
  };

  const handleSave = async () => {
    const { ok, missing } = validateRequired();
    if (!ok) {
      setErrorKeys(new Set(missing.map(m => m.key)));
      const summary = missing.length === 1
        ? `Falta por rellenar: ${missing[0].label}`
        : `Faltan ${missing.length} campos obligatorios. Los hemos marcado en rojo.`;
      toast.error(summary);
      // Open every section with at least one missing field so the user sees them
      const openSections = {};
      for (const m of missing) {
        const sid = m.key.split('.')[0];
        openSections[sid] = true;
      }
      setExpandedSections(prev => ({ ...prev, ...openSections }));
      return;
    }
    setErrorKeys(new Set());
    setSaving(true);
    try {
      if (isCoPromoterMode) {
        // Co-promoters can only autosave; use a final draft save anyway.
        await axios.post(formEndpoint, { data: formData, is_draft: true }, { withCredentials: false });
        toast.success('Tus cambios han quedado guardados. El promotor podrá revisarlos y marcar el formulario como completado.');
      } else {
        await axios.post(formEndpoint, { data: formData, is_draft: false }, { withCredentials: true });
        toast.success(t('clientForm.saved'));
        setExpanded(false);
        onFormCompleted?.();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || t('clientForm.saveError');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Evaluates show_if conditional. Looks at the same section data first
  // (and at repeated-row data when applicable). Coerces booleans so that a
  // checkbox that hasn't been touched (value=undefined) is treated as
  // `false` — that's the common-sense default and the only one that lets
  // "No procede" checkboxes hide/show siblings on first render.
  const isFieldVisible = (field, sectionData) => {
    if (!field.show_if) return true;
    let target = (sectionData || {})[field.show_if.field];
    const expected = field.show_if.equals;
    if (typeof expected === 'boolean') target = !!target;
    return target === expected;
  };

  // Render inline co-promoter aportaciones below a given field (promoter
  // mode only). Shows every approved + pending + rejected suggestion
  // targeting this slot (section + field) with author, status badge, and
  // toggle actions so the promoter can activate/deactivate an aportación
  // at any time — including re-activating one they had previously rejected.
  const renderFieldSuggestions = (sectionId, fieldId) => {
    if (isCoPromoterMode) return null;
    const key = `${sectionId}|${fieldId || '__section__'}`;
    const list = fieldSuggestions[key] || [];
    if (list.length === 0) return null;
    return (
      <div className="mt-2 space-y-1.5" data-testid={`promoter-inline-${sectionId}-${fieldId || 'section'}`}>
        {list.map(s => {
          const valueText = typeof s.value === 'string'
            ? s.value
            : (() => {
                try {
                  return Object.entries(s.value || {})
                    .filter(([k]) => !k.startsWith('_'))
                    .map(([k, v]) => v ? `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}` : null)
                    .filter(Boolean).join(' · ');
                } catch { return String(s.value); }
              })();
          const tone = s.status === 'approved'
            ? { ring: 'border-l-emerald-500 bg-emerald-50/60', label: 'Incluida en el libro', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' }
            : s.status === 'rejected'
            ? { ring: 'border-l-slate-400 bg-slate-50/60 opacity-75', label: 'Desactivada', badge: 'bg-slate-200 text-slate-700 border-slate-300' }
            : { ring: 'border-l-amber-500 bg-amber-50/60', label: 'Aportación pendiente', badge: 'bg-amber-100 text-amber-800 border-amber-200' };
          return (
            <div key={s.suggestion_id} className={`rounded-md border border-l-4 ${tone.ring} p-2 flex items-start gap-2 text-xs`} data-testid={`inline-suggestion-${s.suggestion_id}`}>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground">{s.co_promoter_name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tone.badge}`}>{tone.label}</span>
                </div>
                <p className={`whitespace-pre-wrap ${s.status === 'rejected' ? 'text-muted-foreground line-through' : 'text-foreground/80'}`}>{valueText}</p>
                {s.note && <p className="italic text-muted-foreground text-[11px]">Nota: {s.note}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {(s.status === 'pending' || s.status === 'rejected') && (
                  <Button
                    size="sm" variant="outline" className="h-7 px-2 gap-1"
                    disabled={busySuggestionId === s.suggestion_id}
                    onClick={() => approveFieldSuggestion(s.suggestion_id)}
                    title={s.status === 'rejected' ? 'Volver a incluir esta aportación en el libro' : 'Aceptar e incluir'}
                    data-testid={`inline-approve-${s.suggestion_id}`}
                  >
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    {s.status === 'rejected' ? 'Reactivar' : 'Aceptar'}
                  </Button>
                )}
                {(s.status === 'pending' || s.status === 'approved') && (
                  <Button
                    size="sm" variant="ghost" className="h-7 px-2 gap-1 text-red-600"
                    disabled={busySuggestionId === s.suggestion_id}
                    onClick={() => rejectFieldSuggestion(s.suggestion_id)}
                    title={s.status === 'approved' ? 'Quitar del libro (podrás reactivarla)' : 'Descartar'}
                    data-testid={`inline-reject-${s.suggestion_id}`}
                  >
                    <XCircle className="w-3 h-3" />
                    {s.status === 'approved' ? 'Desactivar' : 'Descartar'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderField = (field, value, onChange, sectionData, contextSectionId) => {
    if (!isFieldVisible(field, sectionData)) return null;
    switch (field.type) {
      case 'info':
        if (field.placeholder === '_motive_summary_placeholder_' && bookProfile) {
          const occLabel = bookProfile.occasion === 'cumple' ? 'Cumpleaños'
            : bookProfile.occasion === 'jubilacion' ? 'Jubilación'
            : bookProfile.occasion === 'boda' ? 'Boda'
            : bookProfile.occasion === 'aniversario' ? 'Aniversario'
            : bookProfile.occasion === 'homenaje' ? 'Homenaje'
            : bookProfile.occasion === 'navidad' ? 'Navidad'
            : bookProfile.occasion === 'otro' ? (bookProfile.occasion_other || 'Otro')
            : '—';
          return (
            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-xs space-y-1">
              <p><span className="font-medium text-amber-900">Ocasión:</span> {occLabel}</p>
              {bookProfile.occasion_detail && (
                <p className="text-amber-900/80 whitespace-pre-line">
                  <span className="font-medium">Detalle:</span> {bookProfile.occasion_detail}
                </p>
              )}
              <p className="text-[10px] text-amber-800 italic mt-2">Puedes ampliar o cambiar esto debajo.</p>
            </div>
          );
        }
        return (
          <p className="text-xs text-muted-foreground italic" data-testid={`client-info-${field.id}`}>
            {field.placeholder || ''}
          </p>
        );
      case 'preview_protagonist_photo':
        return value && value.photo_id ? (
          <div className="flex items-center gap-3" data-testid={`client-preview-${field.id}`}>
            <img
              src={`${API_URL}/api/anecdote-photo/${value.photo_id}`}
              alt="Protagonista"
              className="w-20 h-20 rounded-full object-cover border"
            />
            <p className="text-xs text-muted-foreground">
              Foto del protagonista (la subiste al crear el libro). Si quieres cambiarla, edita los datos del libro.
            </p>
          </div>
        ) : (
          <p className="text-xs text-amber-700">Aún no has subido foto del protagonista.</p>
        );
      case 'text':
        return (
          <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder} className="h-9 text-sm"
            data-testid={`client-field-${field.id}`} />
        );
      case 'date': {
        // Compute the min date the user is allowed to pick. Used by the
        // "Fecha del regalo" field — we need at least 20 days of lead
        // time to produce + ship the book, so we hard-block any earlier
        // date in the picker itself (no need for server-side validation
        // on top — the input's `min` attr is browser-enforced).
        const minDays = parseInt(field.min_days_from_today || 0, 10);
        const today = new Date();
        today.setDate(today.getDate() + minDays);
        const minDate = today.toISOString().slice(0, 10);
        return (
          <div>
            <Input
              type="date"
              value={value || ''}
              min={minDate}
              onChange={(e) => onChange(e.target.value)}
              className="h-9 text-sm"
              data-testid={`client-field-${field.id}`}
            />
            {field.help && (
              <p className="text-[11px] text-muted-foreground mt-1">{field.help}</p>
            )}
          </div>
        );
      }
      case 'textarea':
        return (
          <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder} rows={3} className="text-sm"
            data-testid={`client-field-${field.id}`} />
        );
      case 'select':
        return (
          <Select value={value || undefined} onValueChange={onChange}>
            <SelectTrigger className="h-9 text-sm" data-testid={`client-field-${field.id}`}>
              <SelectValue placeholder={t('common.select')} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'multiselect': {
        const selected = Array.isArray(value) ? value : [];
        const toggle = (v) => {
          if (selected.includes(v)) onChange(selected.filter(x => x !== v));
          else onChange([...selected, v]);
        };
        return (
          <div className="flex flex-wrap gap-1.5" data-testid={`client-field-${field.id}`}>
            {(field.options || []).map(opt => {
              const isOn = selected.includes(opt.value);
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  className={`px-2.5 py-1 rounded-full border text-xs transition ${isOn ? 'bg-primary text-white border-primary' : 'bg-background hover:bg-muted text-foreground border-border'}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );
      }
      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || []).map(opt => {
              const isOn = value === opt.value;
              return (
                <label key={opt.value}
                  className={`flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 transition-colors ${isOn ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/40'}`}>
                  {/* name must be unique per section: all life-stage sections
                      share the field id "info_level" and the browser only
                      allows ONE checked radio per name in the whole page. */}
                  <input type="radio" name={`${contextSectionId || 'form'}_${field.id}`} value={opt.value}
                    checked={isOn}
                    onChange={() => onChange(opt.value)}
                    className="w-4 h-4 accent-primary flex-shrink-0"
                    data-testid={`client-field-${field.id}-${opt.value}`} />
                  <span className={`text-sm ${isOn ? 'font-medium text-primary' : ''}`}>{opt.label}</span>
                </label>
              );
            })}
          </div>
        );
      case 'photos':
        return (
          <AnecdotePhotoUpload
            photos={value || []}
            onChange={onChange}
            bookId={bookId}
            context="client"
            maxPhotos={field.max_photos || 3}
          />
        );
      case 'photos_with_caption': {
        // value: list of { photo: {photo_id, storage_path, ...}, year, place, people, caption, why_special }
        const items = Array.isArray(value) ? value : [];
        const max = field.max_photos || 3;
        return (
          <div className="space-y-3" data-testid={`client-field-${field.id}`}>
            {items.map((it, idx) => (
              <div key={idx} className="border rounded-md p-3 bg-muted/10 space-y-2">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <AnecdotePhotoUpload
                      photos={it.photo ? [it.photo] : []}
                      onChange={(photos) => {
                        const next = [...items];
                        next[idx] = { ...it, photo: photos[0] || null };
                        onChange(next);
                      }}
                      bookId={bookId}
                      context="client"
                      maxPhotos={1}
                    />
                  </div>
                  <Button variant="ghost" size="sm" className="ml-auto"
                    onClick={() => onChange(items.filter((_, i) => i !== idx))}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {it.photo && (
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <div>
                      <Label className="text-[11px] mb-1 block">Año aproximado</Label>
                      <Input value={it.year || ''} placeholder="Ej.: 1998"
                        onChange={(e) => { const n = [...items]; n[idx] = {...it, year: e.target.value}; onChange(n); }}
                        className="h-8 text-sm" />
                    </div>
                    <div>
                      <Label className="text-[11px] mb-1 block">Lugar</Label>
                      <Input value={it.place || ''} placeholder="Ej.: Casa del pueblo"
                        onChange={(e) => { const n = [...items]; n[idx] = {...it, place: e.target.value}; onChange(n); }}
                        className="h-8 text-sm" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[11px] mb-1 block">Quién aparece</Label>
                      <Input value={it.people || ''} placeholder="Personas principales en la foto"
                        onChange={(e) => { const n = [...items]; n[idx] = {...it, people: e.target.value}; onChange(n); }}
                        className="h-8 text-sm" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[11px] mb-1 block">Comentario / Por qué es especial</Label>
                      <Textarea value={it.caption || ''} rows={2}
                        placeholder="Explica brevemente la foto: qué estaba pasando, por qué te llevó a elegirla."
                        onChange={(e) => { const n = [...items]; n[idx] = {...it, caption: e.target.value}; onChange(n); }}
                        className="text-sm" />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {items.length < max && (
              <Button variant="outline" size="sm" onClick={() => onChange([...items, {}])} className="w-full">
                <Plus className="w-3.5 h-3.5 mr-1" /> Añadir foto
              </Button>
            )}
            <p className="text-[11px] text-muted-foreground">
              {items.length}/{max} fotos. Cada foto necesita un breve comentario para que la podamos contextualizar en el libro.
            </p>
          </div>
        );
      }
      case 'protagonist_photo':
        return (
          <ProtagonistPhotoUpload
            photo={value || null}
            onChange={onChange}
            bookId={bookId}
            context="protagonist"
          />
        );
      case 'repeatable_inline': {
        const items = Array.isArray(value) ? value : [];
        const max = field.max_repeat || 5;
        const visible = Math.max(items.length, 0);
        const sectionIdForKey = contextSectionId;
        return (
          <div className="space-y-3" data-testid={`client-field-${field.id}`}>
            {items.map((row, idx) => (
              <div key={idx} className="border rounded-md p-3 bg-muted/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">{field.label} #{idx + 1}</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    const next = items.filter((_, i) => i !== idx);
                    onChange(next);
                  }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {(field.fields || []).map(sub => {
                    if (!isFieldVisible(sub, row)) return null;
                    const setSubVal = (val) => {
                      const next = [...items];
                      next[idx] = { ...next[idx], [sub.id]: val };
                      onChange(next);
                      if (sectionIdForKey) clearError(`${sectionIdForKey}.${field.id}.${idx}.${sub.id}`);
                    };
                    const subKey = sectionIdForKey ? `${sectionIdForKey}.${field.id}.${idx}.${sub.id}` : null;
                    const subMissing = subKey && errorKeys.has(subKey);
                    const colSpan = ['select', 'textarea'].includes(sub.type) ? 'col-span-2' : '';
                    return (
                      <div key={sub.id} className={`${colSpan} ${subMissing ? 'rounded-md ring-2 ring-red-400 bg-red-50/40 p-1.5' : ''}`}>
                        <Label className="text-[11px] mb-1 block">
                          {sub.label}{sub.required && <span className="text-red-500 ml-0.5">*</span>}
                        </Label>
                        {sub.type === 'select' ? (
                          <Select value={row[sub.id] || undefined} onValueChange={setSubVal}>
                            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder={t('common.select')} /></SelectTrigger>
                            <SelectContent>
                              {(sub.options || []).map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : sub.type === 'textarea' ? (
                          <Textarea value={row[sub.id] || ''} onChange={(e) => setSubVal(e.target.value)}
                            placeholder={sub.placeholder} rows={2} className="text-sm" />
                        ) : (
                          <Input value={row[sub.id] || ''} onChange={(e) => setSubVal(e.target.value)}
                            placeholder={sub.placeholder} className="h-8 text-sm" />
                        )}
                        {subMissing && <p className="text-[10px] text-red-600 mt-0.5">Obligatorio.</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {visible < max && (
              <Button variant="outline" size="sm" onClick={() => onChange([...items, {}])} className="w-full">
                <Plus className="w-3.5 h-3.5 mr-1" /> Añadir
              </Button>
            )}
          </div>
        );
      }
      case 'checkbox':
        // Boolean toggle used by "No procede" style fields. Stored as a
        // raw bool so `show_if: {equals: false}` rules work as expected.
        return (
          <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`client-field-${field.id}`}>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              className="w-4 h-4 text-primary rounded"
            />
            <span>{field.label}</span>
          </label>
        );
      case 'anecdote_list': {
        // Anécdotas de la etapa — misma estructura que la del colaborador.
        // Los items antiguos solo llevan {text}: se muestran en "Qué pasó".
        const items = Array.isArray(value) ? value : [];
        const max = field.max_repeat || 10;
        const setItem = (idx, patch) => {
          const next = [...items];
          next[idx] = { ...next[idx], ...patch };
          onChange(next);
        };
        const child = (id) => field.fields?.find((f) => f.id === id) || {};
        const anecSectionTitle = template?.sections?.find((s) => s.id === contextSectionId)?.title || '';
        const buildContext = (item) => {
          const bits = [];
          if (item.title) bits.push(`Título: ${item.title}`);
          if (item.when) bits.push(`Cuándo: ${item.when}`);
          if (item.where) bits.push(`Dónde: ${item.where}`);
          if (item.town) bits.push(`Población: ${item.town}`);
          if (item.who_was_there) bits.push(`Quién estaba: ${item.who_was_there}`);
          if (item.literal_phrase) bits.push(`Frase literal: ${item.literal_phrase}`);
          if (item.how_it_ended) bits.push(`Cómo terminó: ${item.how_it_ended}`);
          return bits.join('\n');
        };
        return (
          <div
            className="rounded-xl border-2 border-primary/40 bg-gradient-to-b from-primary/5 to-amber-50/40 p-4 space-y-3 shadow-sm"
            data-testid={`client-field-${field.id}`}
          >
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h5 className="text-sm font-semibold">{field.label}</h5>
                <p className="text-[11px] text-muted-foreground">
                  {field.placeholder} Son la parte más valiosa del libro: cuantas más cuentes, más vivo será el capítulo.
                </p>
              </div>
            </div>
            {items.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic">
                Aún no has añadido ninguna anécdota de esta etapa.
              </p>
            )}
            {items.map((item, idx) => {
              const whatValue = item.what_happened ?? item.text ?? '';
              return (
              <div key={idx} className="border rounded-md p-3 bg-background space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-primary">
                    Anécdota #{idx + 1}{item.title ? ` — ${item.title}` : ''}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onChange(items.filter((_, i) => i !== idx))}
                    data-testid={`client-anecdote-remove-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div>
                  <Label className="text-[11px] mb-1 block">
                    {child('title').label || 'Título de la anécdota'}<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Input
                    value={item.title || ''}
                    placeholder={child('title').placeholder}
                    onChange={(e) => setItem(idx, { title: e.target.value })}
                    className="h-8 text-sm"
                    data-testid={`client-anecdote-title-${idx}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {['when', 'where', 'town', 'who_was_there'].map((fid) => (
                    <div key={fid}>
                      <Label className="text-[11px] mb-1 block">{child(fid).label || fid}</Label>
                      <Input
                        value={item[fid] || ''}
                        placeholder={child(fid).placeholder}
                        onChange={(e) => setItem(idx, { [fid]: e.target.value })}
                        className="h-8 text-sm"
                        data-testid={`client-anecdote-${fid}-${idx}`}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <Label className="text-[11px] mb-1 block">
                    {child('what_happened').label || '¿Qué pasó?'}<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Textarea
                    rows={3}
                    value={whatValue}
                    placeholder={child('what_happened').placeholder}
                    onChange={(e) => setItem(idx, { what_happened: e.target.value })}
                    className="text-sm"
                    data-testid={`client-anecdote-text-${idx}`}
                  />
                </div>
                <AnecdoteAIAssistant
                  apiBase={apiBase}
                  withCreds={!isCoPromoterMode}
                  text={whatValue}
                  stage={anecSectionTitle}
                  context={buildContext(item)}
                  onApplyText={(newText) => setItem(idx, { what_happened: newText })}
                  onAddPhotos={(chosen) => {
                    const maxP = child('photos').max_photos || 2;
                    const current = Array.isArray(item.photos) ? item.photos : [];
                    const room = Math.max(0, maxP - current.length);
                    const mapped = chosen.slice(0, room).map((ph) => ({
                      photo: { photo_id: ph.photo_id, url: `${API_URL}${ph.url}`, storage_path: ph.storage_path },
                      caption: ph.caption || '',
                    }));
                    if (mapped.length > 0) {
                      setItem(idx, { photos: [...current, ...mapped] });
                      toast.success('Fotos añadidas a la anécdota');
                    } else {
                      toast.message('La anécdota ya tiene el máximo de fotos');
                    }
                  }}
                  testIdPrefix={`panec-${contextSectionId}-${idx}`}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px] mb-1 block">{child('literal_phrase').label || 'Frase literal (si la hay)'}</Label>
                    <Input
                      value={item.literal_phrase || ''}
                      placeholder={child('literal_phrase').placeholder}
                      onChange={(e) => setItem(idx, { literal_phrase: e.target.value })}
                      className="h-8 text-sm"
                      data-testid={`client-anecdote-literal-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] mb-1 block">{child('how_it_ended').label || '¿Cómo terminó o qué significó?'}</Label>
                    <Input
                      value={item.how_it_ended || ''}
                      placeholder={child('how_it_ended').placeholder}
                      onChange={(e) => setItem(idx, { how_it_ended: e.target.value })}
                      className="h-8 text-sm"
                      data-testid={`client-anecdote-ended-${idx}`}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] mb-1 block">
                    {child('certainty').label || '¿Cómo conoces esta anécdota?'}<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {(child('certainty').options || []).map((o) => (
                      <label key={o.value} className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input
                          type="radio"
                          name={`anec-cert-${contextSectionId}-${idx}`}
                          checked={item.certainty === o.value}
                          onChange={() => setItem(idx, { certainty: o.value })}
                          className="w-3.5 h-3.5 accent-primary"
                          data-testid={`client-anecdote-certainty-${o.value}-${idx}`}
                        />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] mb-1 block">Foto(s) de esta anécdota (opcional)</Label>
                  {/* Reuse the photos_with_caption shape: array of photo objects.
                      We render an inline mini version with one photo per slot. */}
                  {renderField(
                    { id: `${field.id}_photos_${idx}`, type: 'photos_with_caption', label: '', max_photos: child('photos').max_photos || 2 },
                    item.photos || [],
                    (photos) => setItem(idx, { photos }),
                    sectionData,
                    contextSectionId,
                  )}
                </div>
              </div>
              );
            })}
            {items.length < max && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onChange([...items, { what_happened: '', photos: [] }])}
                className="w-full border-primary/40 text-primary hover:bg-primary/5"
                data-testid={`client-anecdote-add-${field.id}`}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Añadir otra anécdota
              </Button>
            )}
            <p className="text-[11px] text-muted-foreground">
              {items.length}/{max} anécdotas.
            </p>
          </div>
        );
      }
      default:
        return (
          <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder} className="h-9 text-sm" />
        );
    }
  };

  if (loading || !template) return null;

  const isCompleted = clientFormCompleted;

  // Progress: count only "real" input fields (skip info/preview helpers)
  const isInputField = (f) => !['info', 'preview_protagonist_photo'].includes(f.type);

  // Compute filled/total for a single section. Reused both for the global
  // progress bar and the per-section indicator next to each accordion.
  const calcSectionProgress = (section) => {
    if (!section) return { filled: 0, total: 0, pct: 0 };
    let filled = 0; let total = 0;
    const sectionData = formData[section.id];
    if (section.repeatable) {
      const items = Array.isArray(sectionData) ? sectionData : [];
      for (const item of items) {
        for (const f of section.fields) {
          if (!isInputField(f)) continue;
          if (!isFieldVisible(f, item)) continue;
          total++;
          if (item?.[f.id] && String(item[f.id]).trim()) filled++;
        }
      }
    } else {
      for (const f of section.fields) {
        if (!isInputField(f)) continue;
        if (!isFieldVisible(f, sectionData)) continue;
        total++;
        const val = (sectionData || {})[f.id];
        if (val && (Array.isArray(val) ? val.length > 0 : String(val).trim())) filled++;
      }
    }
    return { filled, total, pct: total > 0 ? Math.round((filled / total) * 100) : 0 };
  };

  const calcProgress = () => {
    if (!template?.sections) return { filled: 0, total: 0, pct: 0 };
    let filled = 0; let total = 0;
    for (const section of template.sections) {
      const sp = calcSectionProgress(section);
      filled += sp.filled;
      total += sp.total;
    }
    return { filled, total, pct: total > 0 ? Math.round((filled / total) * 100) : 0 };
  };
  const progress = calcProgress();

  const autosaveLabel = {
    saving: { text: 'Guardando…', cls: 'text-amber-600' },
    saved: { text: 'Guardado', cls: 'text-green-600' },
    error: { text: 'Error al guardar', cls: 'text-red-600' },
    idle: { text: '', cls: '' },
  }[autosaveStatus];

  return (
    <Card className={`card-elevated mb-8 border-l-4 ${isCompleted ? 'border-l-green-500' : 'border-l-amber-500'}`} data-testid="client-form-panel">
      <CardHeader className="cursor-pointer py-4" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCompleted ? 'bg-green-100' : 'bg-amber-100'}`}>
              {isCompleted ? <Check className="w-5 h-5 text-green-600" /> : <ClipboardList className="w-5 h-5 text-amber-600" />}
            </div>
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                {isCoPromoterMode
                  ? `Hola ${coPromoterName || ''}, ayuda al promotor con el libro`
                  : t('clientForm.title')}
                {isCompleted && <Badge className="bg-green-100 text-green-700 text-[10px]">{t('clientForm.completed')}</Badge>}
                {autosaveLabel.text && (
                  <span className={`text-[11px] inline-flex items-center gap-1 ${autosaveLabel.cls}`} data-testid="client-form-autosave-status">
                    <CheckCircle2 className="w-3.5 h-3.5" />{autosaveLabel.text}
                  </span>
                )}
              </CardTitle>
              <CardDescription className="text-sm">
                {isCompleted ? t('clientForm.completedDesc') : t('clientForm.pendingDesc')}
              </CardDescription>
              {/* Motivational nudge: tells the promoter they can start
                  inviting collaborators without finishing the entire form.
                  Reduces the "I'll do this when I have a free afternoon"
                  effect that kills completion. */}
              {!isCompleted && !isCoPromoterMode && progress.pct > 5 && progress.pct < 100 && (
                <div className="mt-2 flex items-start gap-2 text-[11px] text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-2 py-1.5">
                  <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-blue-600" />
                  <span>
                    Has rellenado un {progress.pct}%. <strong>Ya puedes invitar a tus colaboradores</strong> mientras sigues completando el resto — el auto-guardado se encarga de que no pierdas nada.
                  </span>
                </div>
              )}
              <div className="mt-2 flex items-center gap-3">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden" data-testid="client-form-progress-bar">
                  <div className={`h-full rounded-full transition-all duration-500 ${progress.pct === 100 ? 'bg-green-500' : progress.pct > 50 ? 'bg-primary' : 'bg-amber-500'}`}
                    style={{ width: `${progress.pct}%` }} />
                </div>
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap" data-testid="client-form-progress-text">
                  {progress.filled}/{progress.total} ({progress.pct}%)
                </span>
              </div>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 space-y-6">
          {/* Welcome modal — first-visit only */}
          {showWelcome && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="welcome-modal">
              <div className="bg-background rounded-xl max-w-md w-full p-6 shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold">Bienvenido al formulario biográfico</h3>
                </div>
                <div className="text-sm text-muted-foreground space-y-3">
                  <p>Este libro será mucho más que una recopilación de recuerdos: <strong>un retrato vivo</strong> contado a través de la historia y la mirada de quienes le quieren.</p>
                  <p>El formulario es <strong>extenso</strong> (puede llevarte 30-60 minutos). No hace falta que lo termines de una sentada:</p>
                  <ul className="list-disc pl-5 space-y-1 text-xs">
                    <li>Tus respuestas se guardan automáticamente cada vez que escribes.</li>
                    <li>Puedes salir y volver cuando quieras.</li>
                    <li>Si necesitas ayuda, añade <strong>co-promotores</strong> en la sección "Relación contigo": recibirán acceso al mismo formulario.</li>
                    <li>Empieza por las etapas que mejor conozcas. Las que no recuerdes puedes dejarlas en blanco.</li>
                  </ul>
                </div>
                <Button onClick={dismissWelcome} className="w-full mt-5" data-testid="dismiss-welcome">
                  Empezar
                </Button>
              </div>
            </div>
          )}

          {/* Co-promoter management panel (promoter mode only) */}
          {!isCoPromoterMode && coPromoters.length > 0 && (
            <div className="border rounded-lg p-4 bg-blue-50/30" data-testid="co-promoters-panel">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-blue-600" />
                Co-promotores ({coPromoters.length})
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                Estas personas tienen permiso para editar este formulario. Puedes revocar o restaurar sus permisos cuando quieras.
              </p>
              <div className="space-y-2">
                {coPromoters.map(cp => (
                  <div key={cp.contributor_id}
                    className="flex items-center justify-between bg-background rounded-md px-3 py-2 border text-sm">
                    <div>
                      <p className="font-medium">{cp.name} {cp.last_name || ''}</p>
                      <p className="text-xs text-muted-foreground">{cp.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={cp.co_promoter_active
                        ? 'bg-green-100 text-green-700 hover:bg-green-100'
                        : 'bg-red-100 text-red-700 hover:bg-red-100'}>
                        {cp.co_promoter_active ? 'Activo' : 'Revocado'}
                      </Badge>
                      <Button variant="ghost" size="sm"
                        onClick={() => toggleCoPromoter(cp.contributor_id, !cp.co_promoter_active)}
                        data-testid={`toggle-cp-${cp.contributor_id}`}>
                        {cp.co_promoter_active
                          ? <><UserX className="w-3.5 h-3.5 mr-1" />Revocar</>
                          : <><UserCheck className="w-3.5 h-3.5 mr-1" />Restaurar</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {template.sections.map((section) => {
            const isSectionExpanded = expandedSections[section.id];
            const isRepeatable = section.repeatable;
            const repeats = isRepeatable
              ? (repeatCounts[section.id] || ((formData[section.id] || []).length || 1))
              : 1;
            const sectionData = formData[section.id] || (isRepeatable ? [] : {});
            // Per-section completion — color reflects status: not-started
            // (grey), in-progress (amber), done (green) — keeps the form
            // feeling alive and motivates completing one stage at a time.
            const secProgress = calcSectionProgress(section);
            const secColor = secProgress.pct === 100
              ? 'bg-emerald-500'
              : secProgress.pct > 0
                ? 'bg-amber-500'
                : 'bg-muted-foreground/30';
            const secBadgeColor = secProgress.pct === 100
              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
              : secProgress.pct > 0
                ? 'bg-amber-100 text-amber-700 border-amber-200'
                : 'bg-gray-100 text-gray-600 border-gray-200';

            return (
              <div key={section.id} className="border rounded-lg overflow-hidden" data-testid={`client-section-${section.id}`}>
                <div className="flex items-center justify-between px-4 py-3 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors gap-3"
                  onClick={() => toggleSection(section.id)}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-sm">{section.title}</h4>
                      {secProgress.total > 0 && (
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${secBadgeColor}`}
                          data-testid={`section-progress-badge-${section.id}`}
                        >
                          {secProgress.pct}%
                        </Badge>
                      )}
                    </div>
                    {section.description && <p className="text-xs text-muted-foreground whitespace-pre-line">{section.description}</p>}
                    {secProgress.total > 0 && (
                      <div className="mt-1.5 h-1 rounded-full bg-muted overflow-hidden max-w-xs">
                        <div
                          className={`h-full transition-all duration-300 ${secColor}`}
                          style={{ width: `${secProgress.pct}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isRepeatable && (
                      <Badge variant="outline" className="text-[10px]">
                        {repeats}/{section.max_repeat}
                      </Badge>
                    )}
                    {isSectionExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>

                {isSectionExpanded && (
                  <div className="p-4 space-y-4">
                    {isRepeatable ? (
                      <>
                        {Array.from({ length: repeats }).map((_, idx) => {
                          const rowData = (sectionData)[idx] || {};
                          return (
                            <div key={idx} className={`${idx > 0 ? 'pt-3 border-t' : ''}`}>
                              <div className="flex items-center justify-between mb-2">
                                {repeats > 1 && (
                                  <p className="text-xs font-medium text-primary">{section.title} {idx + 1}</p>
                                )}
                                {repeats > 1 && (
                                  <Button variant="ghost" size="sm" onClick={() => removeRepeatItem(section.id, idx)} data-testid={`client-remove-${section.id}-${idx}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </div>
                              <div className="space-y-3">
                                {section.fields.map(field => {
                                  if (!isFieldVisible(field, rowData)) return null;
                                  const fkey = `${section.id}.${idx}.${field.id}`;
                                  const isMissing = errorKeys.has(fkey);
                                  return (
                                  <div key={field.id}
                                    className={isMissing ? 'rounded-md ring-2 ring-red-400 bg-red-50/40 p-2' : ''}>
                                    <Label className="text-xs mb-1 block">
                                      {field.label}
                                      {field.required && <span className="text-red-500 ml-0.5">*</span>}
                                    </Label>
                                    {renderField(
                                      field,
                                      rowData[field.id],
                                      (val) => updateRepeatField(section.id, idx, field.id, val),
                                      rowData,
                                      section.id,
                                    )}
                                    {isMissing && <p className="text-[11px] text-red-600 mt-1">Este campo es obligatorio.</p>}
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        {repeats < section.max_repeat && (
                          <Button variant="outline" size="sm" className="w-full text-xs"
                            onClick={() => addRepeatItem(section.id, section.max_repeat)}
                            data-testid={`client-add-${section.id}`}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> {t('clientForm.addAnother')}
                          </Button>
                        )}
                        {renderFieldSuggestions(section.id, null)}
                      </>
                    ) : (
                      <div className="space-y-3">
                        {section.fields.map(field => {
                          if (!isFieldVisible(field, sectionData)) return null;
                          // In co-promoter mode, hide the helpers list (they can't add more co-promoters)
                          if (hideHelpersSection && field.id === 'helpers_list') return null;
                          if (hideHelpersSection && field.id === '_helpers_help') return null;
                          const isInline = field.type === 'repeatable_inline' || field.type === 'anecdote_list';
                          const fkey = `${section.id}.${field.id}`;
                          const isMissing = errorKeys.has(fkey);
                          return (
                            <div key={field.id}
                              className={isMissing ? 'rounded-md ring-2 ring-red-400 bg-red-50/40 p-2' : ''}>
                              {!isInline && (
                                <Label className="text-xs mb-1 block">
                                  {field.label}
                                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                                </Label>
                              )}
                              {renderField(
                                field,
                                sectionData[field.id],
                                (val) => updateField(section.id, field.id, val),
                                sectionData,
                                section.id,
                              )}
                              {isMissing && <p className="text-[11px] text-red-600 mt-1">Este campo es obligatorio.</p>}
                              {renderFieldSuggestions(section.id, field.id)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex items-center justify-between pt-2">
            <span className={`text-xs ${autosaveLabel.cls}`}>{autosaveLabel.text}</span>
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-client-form-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t('clientForm.saving') : (isCoPromoterMode ? 'Guardar mis cambios' : 'Marcar formulario como completado')}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default ClientFormPanel;
